import { Ops } from '@willie/schemas'
import { parseStoredImageRef } from '@willie/supabase/storage'
import type { TRPCOutputs } from '~/utils/trpc'
import { downloadBlob } from '~/utils/downloadBlob'

const isOpsHosted = (storedRef: string): boolean => parseStoredImageRef(storedRef).kind === 'opsPath'

export type Installation = TRPCOutputs['ops']['stationInstallations']['list'][number]

export interface ExportContext {
  installation: Installation
  orgName: string
  /** Stored image ref → signed URL, from signImageRefsForProviderOrder. */
  signedImageUrls: SignedImageUrls
}

export type SignedImageUrls = Record<string, string>

export interface ExportColumn {
  label: string
  get: (ctx: ExportContext) => string | null
  required?: boolean
}

export interface ExportSpec {
  buttonLabel: string
  filenamePrefix: string
  columns: ExportColumn[]
  filter?: (installation: Installation) => boolean
  /**
   * Whether any column emits a signed image URL. Declared rather than inferred so a
   * spec without one never pays a storage round-trip — and, more importantly, so a
   * signing failure cannot block an export whose CSV carries no image at all.
   */
  needsSignedImages?: boolean
}

export interface MissingFieldsRow {
  installationId: number
  installationName: string | null
  missingColumns: string[]
}

type ResolveOrgName = (installation: Installation) => string

const meterCompatibility = (installation: Installation): string => {
  if (installation.meter_compatibility === null) return '⏳ En attente'
  if (installation.meter_compatibility === 'compatible') return '✅ Compatible'
  return '🔍 Under analysis'
}

const meterAddress = (installation: Installation): string | null => {
  const siteName = installation.site?.name?.trim()
  const address = installation.site_contact?.address?.trim()
  if (!address) return siteName ?? null
  return siteName ? `${siteName}, ${address}` : address
}

export const SHAYP_EXPORT: ExportSpec = {
  buttonLabel: 'Shayp Export',
  filenamePrefix: 'shayp-export',
  // Not yet installed, rather than not yet linked: every installation owns a station from creation.
  filter: (installation) => !installation.logger_shipping_date && !installation.installation_date,
  needsSignedImages: true,
  columns: [
    { label: 'Project Name', get: ({ orgName }) => orgName },
    { label: 'Meter name', get: ({ installation }) => String(installation.installation_id) },
    { label: 'Internal ID', get: ({ installation }) => installation.internal_id },
    { label: 'Meter Compatibility', get: ({ installation }) => meterCompatibility(installation) },
    {
      label: 'Attachements',
      // The provider opens this CSV days-to-weeks later with no Willie session, and the
      // ops bucket is private — so the cell has to carry a long-lived signed URL, minted
      // before the CSV is built. A third-party-hosted image is passed through unchanged.
      // Anything of ours that failed to sign yields null so the required-column gate
      // stops the order, rather than mailing out a path the provider cannot open.
      get: ({ installation, signedImageUrls }) => {
        const stored = installation.pre_installation_image_url
        if (!stored) return null
        return signedImageUrls[stored] ?? (isOpsHosted(stored) ? null : stored)
      },
      required: true
    },
    {
      label: 'Contact email',
      get: ({ installation }) => `orders+${installation.installation_id}@transactional.meetwillie.com`
    },
    {
      label: 'Contact phone number',
      get: ({ installation }) => installation.site_contact?.phone ?? null,
      required: true
    },
    {
      label: 'Contact person',
      get: ({ installation }) => installation.site_contact?.name ?? null,
      required: true
    },
    { label: 'Meter adress', get: ({ installation }) => meterAddress(installation), required: true },
    { label: 'Planned installation date', get: ({ installation }) => installation.planned_installation_date }
  ]
}

export const EFENTO_EXPORT: ExportSpec = {
  buttonLabel: 'Efento Export',
  filenamePrefix: 'efento-export',
  filter: (i) => i.logger_maker === 'efento',
  columns: [
    { label: 'installationId', get: ({ installation }) => String(installation.installation_id) },
    { label: 'clientOrg', get: ({ orgName }) => orgName },
    { label: 'stationName', get: ({ installation }) => installation.name },
    {
      label: 'installationProfile',
      get: ({ installation }) => installation.installation_profile,
      required: true
    },
    {
      label: 'assemblyProfile',
      get: ({ installation }) =>
        Ops.InstallationProfileSkus.installationProfileAssemblyProfile(installation.installation_profile)
    },
    { label: 'shippingName', get: ({ installation }) => installation.site_contact?.name ?? null, required: true },
    {
      label: 'shippingPhone',
      get: ({ installation }) => installation.site_contact?.phone ?? null,
      required: true
    },
    {
      label: 'shippingAdress',
      get: ({ installation }) => installation.site_contact?.address ?? null,
      required: true
    },
    {
      label: 'shippingEmail',
      get: ({ installation }) => `orders+${installation.installation_id}@transactional.meetwillie.com`
    },
    {
      label: 'material',
      get: ({ installation }) => {
        const skus = Ops.InstallationProfileSkus.installationProfileSkus(installation.installation_profile)
        return skus ? JSON.stringify(skus.map(Ops.InstallationProfileSkus.supplierArticle)) : null
      },
      required: true
    }
  ]
}

const escapeCsvField = (value: string | null | undefined): string => {
  const str = value ?? ''
  return /[",\n\r]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str
}

export const findMissing = (
  spec: ExportSpec,
  installations: Installation[],
  resolveOrgName: ResolveOrgName,
  signedImageUrls: SignedImageUrls = {}
): MissingFieldsRow[] => {
  const requiredColumns = spec.columns.filter((c) => c.required)
  return installations
    .map((installation) => ({
      installationId: installation.installation_id,
      installationName: installation.name,
      missingColumns: requiredColumns
        .filter(
          ({ get }) => !get({ installation, orgName: resolveOrgName(installation), signedImageUrls })?.trim()
        )
        .map(({ label }) => label)
    }))
    .filter((row) => row.missingColumns.length > 0)
}

export const buildCsv = (
  spec: ExportSpec,
  installations: Installation[],
  resolveOrgName: ResolveOrgName,
  signedImageUrls: SignedImageUrls = {}
): string => {
  const header = spec.columns.map((c) => escapeCsvField(c.label)).join(',')
  const rows = installations.map((installation) =>
    spec.columns
      .map((c) => escapeCsvField(c.get({ installation, orgName: resolveOrgName(installation), signedImageUrls })))
      .join(',')
  )
  return [header, ...rows].join('\n')
}

export const downloadCsv = (filename: string, content: string): void => {
  downloadBlob(filename, new Blob([content], { type: 'text/csv;charset=utf-8' }))
}

export type OrderableProvider = Ops.StationInstallations.OrderableProvider

export const ORDERABLE_MAKERS = Ops.StationInstallations.orderableProviderSchema.options

export const isOrderable = (maker: string | null): maker is OrderableProvider =>
  ORDERABLE_MAKERS.some((m) => m === maker)

export const ORDER_EXPORT_SPEC: Record<OrderableProvider, ExportSpec> = {
  shayp: SHAYP_EXPORT,
  efento: EFENTO_EXPORT
}
